import { useState } from "react";
import { Download, Share2, Twitter, Linkedin, Loader2, Check } from "lucide-react";
import { BACKEND_URL } from "../lib/api";
import { logger } from "../lib/logger";

/**
 * Renders share buttons under an autopsy or roast result.
 * - "Download card"  → fetches the server-rendered PNG and saves it
 * - "Share on X"     → opens Twitter intent with pre-written copy + link
 * - "Share on LinkedIn" → opens LinkedIn share
 * - Native Web Share API on mobile when supported
 *
 * The PNG includes a copyforge.llc/try watermark — every share is a free ad.
 */
export function ShareCardActions({ kind = "autopsy", data, refCode = "", className = "" }) {
  const [downloading, setDownloading] = useState(false);
  const [shared, setShared] = useState(false);

  // Build the URL we want people to land on when they click the shared post
  const trySlug = refCode ? `/try?ref=${encodeURIComponent(refCode)}` : "/try";
  const shareUrl = `https://copyforge.llc${trySlug}`;

  // Pre-written share copy
  const SHARE_TEXT = kind === "roast"
    ? `Just had my draft roasted by an AI. Brutal but useful. Free demo:`
    : `I just dissected a viral tweet. Got back the hook type, psychological trigger, and a template I can steal. Free demo, no signup:`;

  const fetchImage = async () => {
    const r = await fetch(`${BACKEND_URL}/api/share/card`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind, ref_code: refCode || undefined, ...data }),
    });
    if (!r.ok) throw new Error(`Card render failed: ${r.status}`);
    return await r.blob();
  };

  const download = async () => {
    setDownloading(true);
    try {
      const blob = await fetchImage();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `copyforge-${kind}-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      logger.error("download share card", e);
    } finally {
      setDownloading(false);
    }
  };

  // Native share (mobile) — image + text + url in one tap
  const nativeShare = async () => {
    try {
      const blob = await fetchImage();
      const file = new File([blob], "copyforge-share.png", { type: "image/png" });
      const payload = {
        text: `${SHARE_TEXT} ${shareUrl}`,
        title: kind === "roast" ? "Roast my copy" : "Viral Autopsy",
        url: shareUrl,
      };
      // Try sharing with the image attached first (best UX)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ ...payload, files: [file] });
      } else if (navigator.share) {
        await navigator.share(payload);
      } else {
        await download();
      }
      setShared(true);
      setTimeout(() => setShared(false), 2500);
    } catch (e) {
      // user cancelled or unsupported — fall back to download
      if (e?.name !== "AbortError") {
        logger.error("native share", e);
        await download();
      }
    }
  };

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(shareUrl)}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  const supportsNative = typeof navigator !== "undefined" && (navigator.share || navigator.canShare);

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="share-card-actions">
      {supportsNative && (
        <button
          onClick={nativeShare}
          data-testid="share-native-btn"
          className="bg-brand text-white text-sm font-semibold px-3 py-2 hover:bg-brand-hover flex items-center gap-1.5"
        >
          {shared ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
          {shared ? "Shared!" : "Share"}
        </button>
      )}
      <button
        onClick={download}
        disabled={downloading}
        data-testid="share-download-btn"
        className="bg-zinc-900 dark:bg-white dark:text-zinc-950 text-white text-sm font-semibold px-3 py-2 hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
      >
        {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        {downloading ? "Rendering…" : "Download card"}
      </button>
      <a
        href={twitterUrl}
        target="_blank"
        rel="noreferrer"
        data-testid="share-twitter-btn"
        className="bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 text-sm font-semibold px-3 py-2 hover:bg-zinc-200 dark:hover:bg-zinc-700 flex items-center gap-1.5"
      >
        <Twitter className="w-4 h-4" />
        Post on X
      </a>
      <a
        href={linkedinUrl}
        target="_blank"
        rel="noreferrer"
        data-testid="share-linkedin-btn"
        className="bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 text-sm font-semibold px-3 py-2 hover:bg-zinc-200 dark:hover:bg-zinc-700 flex items-center gap-1.5"
      >
        <Linkedin className="w-4 h-4" />
        LinkedIn
      </a>
    </div>
  );
}
